import { useCallback, useEffect, useState, type JSX } from 'react'

import { useTranslation } from '../i18n.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { FrameOption } from './FrameChoice.js'
import { StockHolds, type HoldView } from './StockHolds.js'

/**
 * The holds panel, wired. §6.33 `RES-001`, `RES-002`; `decisions/0032`.
 *
 * `StockHolds` is a view over data and this is the part that has a queue and a loader, which is
 * the split `RxActions` already uses: a screen that holds a transport is a screen that cannot be
 * tested without one.
 *
 * **Both writes go through the queue** (`OFF-001a`). No direct API call from a component, which is
 * what makes full offline a contained change later instead of a rewrite.
 *
 * **The list is re-read after a write rather than patched here.** The server decides what a hold
 * looks like after it is placed, and a local patch is a second opinion that drifts the first time
 * the sweep expires something between the write and the read.
 */

export interface CustomerHoldsProps {
  readonly customerId: string
  readonly load: (customerId: string) => Promise<readonly HoldView[]>
  readonly loadFrames: (query: string) => Promise<readonly FrameOption[]>
  readonly canHold: boolean
  /** The branch's zone, so every instant on the panel is the shop's clock and not the device's. */
  readonly timezone: string
  readonly nowIso: string
}

export function CustomerHolds({
  customerId,
  load,
  loadFrames,
  canHold,
  timezone,
  nowIso,
}: CustomerHoldsProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [holds, setHolds] = useState<readonly HoldView[] | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  /** Bumped once a write settles, so the frame picker re-reads a shelf the write just changed. */
  const [shelfVersion, setShelfVersion] = useState(0)

  const read = useCallback(() => {
    load(customerId).then(
      (loaded) => {
        setHolds(loaded)
        setLoadError(null)
      },
      (cause: unknown) => {
        // Kept apart from the list: a failed read stored as an empty one tells the dispenser no
        // frame is promised to this customer, which is the one thing it cannot know.
        setLoadError(cause instanceof Error ? cause.message : t('holds.loadFailed'))
      },
    )
  }, [customerId, load, t])

  useEffect(read, [read])
  useWriteSettled(snapshot, writeId, () => {
    read()
    setShelfVersion((n) => n + 1)
  })

  return (
    <StockHolds
      holds={holds}
      {...(loadError === null ? {} : { loadError })}
      onRetry={() => {
        setLoadError(null)
        read()
      }}
      loadFrames={loadFrames}
      canHold={canHold}
      timezone={timezone}
      nowIso={nowIso}
      shelfVersion={shelfVersion}
      onHold={(input) => {
        void (async () => {
          const write = (await enqueue({
            method: 'POST',
            path: `/stock/items/${input.stockItemId}/holds`,
            label: t('holds.hold'),
            body: {
              customerId,
              quantity: input.quantity,
              expiresAt: input.expiresAt,
            },
          })) as { id: string }
          setWriteId(write.id)
        })()
      }}
      onRelease={(input) => {
        void (async () => {
          const write = (await enqueue({
            method: 'POST',
            path: `/stock/reservations/${input.reservationId}/release`,
            label: t('holds.release'),
            body: { reason: input.reason, expectedVersion: input.expectedVersion },
          })) as { id: string }
          setWriteId(write.id)
        })()
      }}
    />
  )
}
